import styles from 'css/src/components/public-pages/themes-page/PublicThemesPage.module.scss';

import React from 'react';
import { useSelector } from 'react-redux';
import { VideoData } from '@lumen5/luminary';

import type { Template } from 'js/src/client';
import { Urls } from 'js/src/client';
import TemplateCategorySelectionList from 'js/src/components/app/template-preview/TemplateCategorySelectionList';
import type { TemplateGallerySettings } from 'js/src/components/app/template-preview/TemplateGalleryContext';
import { TemplateGalleryProvider } from 'js/src/components/app/template-preview/TemplateGalleryContext';
import TemplatePreviewModal, {
    TEMPLATE_PREVIEW_VARIANTS,
} from 'js/src/components/app/template-preview/TemplatePreviewModal';
import {
    getTemplateById,
    loadTemplateAndSceneDesigns,
    loadTemplatesAndCategories,
} from 'js/src/data/templates';
import type { ImmutableMap } from 'js/src/libs/consts';
import Navigation from 'js/src/libs/navigation';
import { useAppDispatch } from 'js/src/libs/RootState';
import type { RootState } from 'js/src/libs/RootState';
import { LuminaryContextProvider } from 'js/src/luminary';

export function templateIdFromPath(pathname: string): string | null {
    if (!pathname.startsWith(Urls.THEMES_PAGE)) return null;
    return pathname.slice(Urls.THEMES_PAGE.length).replace(/\/$/, '') || null;
}

function themePath(templateId: string): string {
    return `${Urls.THEMES_PAGE}${encodeURIComponent(templateId)}/`;
}

/**
 * Mirrors the `{% block title %}` in web/templates/public/themes-page.html, so opening the
 * modal client-side lands on the same title a hard reload of /themes/<id>/ would render.
 * Keep the two in sync - PublicThemesPage.spec.tsx pins the exact strings.
 */
export function documentTitleForTheme(themeName: string | null): string {
    const trimmed = themeName?.trim() ?? '';
    if (!trimmed) return 'Free Video Themes | Lumen5';
    return `${trimmed} Video Theme | Lumen5`;
}

const THEME_GALLERY: TemplateGallerySettings = { getTemplateHref: themePath };

type PublicThemesPageProps = {
    /**
     * The theme this URL is about, from the server, rather than read back off the page.
     */
    initialTemplateId: string | null;
};

const PublicThemesPage: React.FC<PublicThemesPageProps> = ({ initialTemplateId }) => {
    const dispatch = useAppDispatch();
    const [openTemplateId, setOpenTemplateId] = React.useState<string | null>(initialTemplateId);
    const openTemplate = useSelector((state: RootState) =>
        openTemplateId ? getTemplateById(state, openTemplateId) : undefined,
    );

    React.useEffect(() => {
        dispatch(loadTemplatesAndCategories());
    }, [dispatch]);

    // A deep-linked theme isn't necessarily in the list response yet, and the modal can't
    // render until it's in the store.
    React.useEffect(() => {
        if (!openTemplateId) return;
        dispatch(loadTemplateAndSceneDesigns(openTemplateId)).catch(() => {
            // Already reported by the operation; the modal just stays closed.
        });
    }, [dispatch, openTemplateId]);

    React.useEffect(() => {
        // Read the path rather than history.state, which is null on the initial entry and
        // after a hard reload.
        const handlePopState = () =>
            setOpenTemplateId(templateIdFromPath(window.location.pathname));
        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, []);

    const openThemeName = openTemplate?.get('display_name') ?? null;
    React.useEffect(() => {
        // A deep-linked theme isn't in the store on the first render, and the server
        // already rendered its title. Setting the gallery title here would flash the wrong
        // one in the tab until the fetch lands.
        if (openTemplateId && !openThemeName) return;
        document.title = documentTitleForTheme(openThemeName);
    }, [openTemplateId, openThemeName]);

    const handleThemeClick = React.useCallback((template: ImmutableMap<Template>) => {
        const templateId = template.get('id');
        setOpenTemplateId(templateId);
        window.history.pushState({ templateId }, '', themePath(templateId));
        // The theme renders at the top of the page rather than over it, so a click from
        // halfway down the gallery would otherwise change nothing the visitor can see.
        window.scrollTo({ top: 0, behavior: 'smooth' });
        analytics.track('Public Themes: Theme Previewed', { themeId: templateId });
    }, []);

    const handleClose = React.useCallback(() => {
        setOpenTemplateId(null);
        window.history.pushState({ templateId: null }, '', Urls.THEMES_PAGE);
    }, []);

    const handleUseTheme = React.useCallback(() => {
        analytics.track(
            'Public Themes: Signup Redirect',
            { entry: 'use_theme', themeId: openTemplateId },
            () => Navigation.goToLocation(Urls.SIGNUP),
        );
    }, [openTemplateId]);

    return (
        <LuminaryContextProvider
            minDimensionResolution={215}
            mediaSizeToUse={VideoData.THUMBNAIL_MEDIA_SIZE}
            fetchAudio={false}
            fetchFonts={true}
            fetchMedia={true}
            fetchTransitions={false}
            disableCaptions={true}
            loadStatusMaxCount={1}
        >
            <div className={styles['themes-page']}>
                <div className="text-center px-l5-16 pt-l5-48 pb-l5-32">
                    <h1 className="font-l5 font-l5--h2 mb-l5-8">
                        {openThemeName ? `${openThemeName} video theme` : 'Video themes'}
                    </h1>
                    {!openThemeName && (
                        <p
                            className={`${styles['hero-subtitle']} font-l5 font-l5--regular mb-l5-0`}
                        >
                            Professionally designed, ready to use. Preview every scene, pick your
                            format, and start creating for free.
                        </p>
                    )}
                </div>
                {openTemplate && !openTemplate.isEmpty() && (
                    <>
                        <TemplatePreviewModal
                            key={openTemplateId}
                            inline
                            isVisible
                            variant={TEMPLATE_PREVIEW_VARIANTS.PUBLIC}
                            template={openTemplate}
                            onTemplateSelection={handleUseTheme}
                            onClose={handleClose}
                        />
                        <h2 className="font-l5 font-l5--h4 text-center mt-l5-64 mb-l5-32">
                            All video themes
                        </h2>
                    </>
                )}
                <TemplateGalleryProvider value={THEME_GALLERY}>
                    <TemplateCategorySelectionList
                        onTemplateClick={handleThemeClick}
                        useWindowScroll
                    />
                </TemplateGalleryProvider>
            </div>
        </LuminaryContextProvider>
    );
};

export default PublicThemesPage;
